import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import type { EntryComponent, IRenderContext } from 'cms/entries/entries';
import type { ILpComponentLwcHeroCard } from 'declarations/contentful';

import { generatePresetPictureSet } from '../shared/performance/pictures';
import type { PresetPictureSet } from '../shared/performance/pictures';
import Grid from './Grid';

const slugify = (str: string) =>
  str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

import styles from './LWCHeroCard.module.css';

// Hardcoded breakpoints from @opendoor/bricks/theme/novo: [0, 375, 768, 1201, 1440]
const PICTURE_SIZES: PresetPictureSet = [
  {
    minWidth: 0,
    maxWidth: 375,
    preset: 'square-256',
  },
  {
    minWidth: 375,
    maxWidth: 768,
    preset: 'square-512',
  },
  {
    minWidth: 768,
    maxWidth: 1201,
    preset: 'square-512',
  },
  {
    minWidth: 1201,
    maxWidth: null,
    preset: 'square-1024',
  },
];

const RenderLWCHeroCard = (
  entry: ILpComponentLwcHeroCard,
  _resolvedData?: unknown,
  context?: IRenderContext,
) => {
  const { fields } = entry;
  const isVideoAsset = fields?.clipImage && fields?.videoUrl;
  const isFirstBlockEntry = Boolean(context?.isFirstBlockEntry);
  const titleSlug = fields?.title ? slugify(fields.title) : undefined;

  const containerClassName = [
    styles.containerMinHeight,
    isVideoAsset ? styles.containerVideoMaxHeight : styles.containerImageMaxHeight,
  ].join(' ');

  return (
    <Col analyticsName={`cosmos-landing-page-lwc-hero-card-${fields?.analyticsId}`} trackImpression>
      <Grid>
        <Col
          id={titleSlug || undefined}
          className={cn(
            containerClassName,
            'col-span-12 scroll-mt-24 overflow-hidden rounded-4xl bg-od-bg-secondary',
            'mb-5 h-154 items-center justify-between gap-8 pb-8 pl-8 pr-8',
            'md:mb-6 md:h-96 md:flex-row md:items-start md:gap-3 md:pl-16 md:pr-0 md:pb-0',
            'lg:h-154 lg:pl-20',
          )}
        >
          <Col className="w-full justify-between px-0 pt-8 md:h-full md:max-w-78 md:pt-16 md:pb-16 lg:max-w-125 lg:pt-20 lg:pb-20">
            <Col className={cn(!fields?.heroCardName && 'hidden', 'pb-12 md:flex')}>
              <Text
                as="p"
                className="block text-body-lg font-medium text-od-content-primary lg:text-subheader-md"
              >
                {fields.heroCardName}
              </Text>
            </Col>
            <Col className="w-full gap-5 md:w-5/6">
              <Col>
                <Text
                  as="p"
                  className="block text-subheader-md text-od-content-primary md:text-header-sm lg:text-header-md"
                >
                  {fields.title}
                </Text>
                {fields.titleAccent && (
                  <Text
                    as="p"
                    className="block text-subheader-md text-od-content-inactive-primary md:text-header-sm lg:text-header-md"
                  >
                    {fields.titleAccent}
                  </Text>
                )}
              </Col>
              {fields.body && (
                <Text as="p" className="block text-body-sm text-od-content-primary">
                  {fields.body}
                </Text>
              )}
            </Col>
          </Col>
          <Col className="relative h-full w-full shrink overflow-hidden rounded-3xl md:min-h-78 md:rounded-none">
            {/* eslint-disable-next-line no-nested-ternary -- Grandfathered violation */}
            {isVideoAsset ? (
              <Row
                className={cn(
                  styles.videoFlexWrapper,
                  'h-full w-full items-end justify-center md:justify-end md:pt-12',
                )}
              >
                <Row className="h-full w-full max-h-108 justify-center overflow-hidden md:min-w-71 md:justify-end lg:max-h-none lg:min-w-125">
                  <video
                    className={styles.video}
                    height="100%"
                    width="100%"
                    src={fields?.videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload={isFirstBlockEntry ? 'auto' : 'metadata'}
                  />
                </Row>
              </Row>
            ) : fields?.cardImage ? (
              <Col
                className={cn(
                  styles.imagePositionWrapper,
                  'absolute bottom-0 left-0 h-full w-full',
                )}
              >
                {generatePresetPictureSet({
                  photoUrl: fields?.cardImage,
                  sizes: PICTURE_SIZES,
                  debugImageSizes: false,
                  preload: isFirstBlockEntry,
                  lazy: !isFirstBlockEntry,
                  objectFit: 'cover',
                })}
              </Col>
            ) : null}
          </Col>
        </Col>
      </Grid>
    </Col>
  );
};

const LWCHeroCard: EntryComponent<ILpComponentLwcHeroCard> = {
  render: RenderLWCHeroCard,
};

export default LWCHeroCard;
